import type { ReactNode } from "react";
import cx from "ui/cx";
import InputLabel from "ui/forms/InputLabel";
import IconInfoLine from "ui/icons/IconInfoLine";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./InputGroup.module.scss";

export interface InputGroupProps {
  labelText?: ReactNode;
  labelLink?: ReactNode;
  tooltip?: ReactNode;
  className?: string;
  children: ReactNode;
  errorText?: string;
  required?: boolean;
  id?: string;
}

export default function InputGroup({
  labelText,
  labelLink,
  tooltip,
  className,
  children,
  errorText,
  required,
  id,
}: InputGroupProps) {
  return (
    <div className={cx(styles.wrapper, className)}>
      {labelText && (
        // The tooltip icon is a sibling of the <label>, never a descendant: an accessible name on
        // a descendant joins the label's own name computation, so the labelled control would
        // answer to "<label text> More information". Sitting outside also means a click on the
        // icon is not label activation, so the `htmlFor` control does not focus or toggle.
        <div className={styles.labelRow}>
          <label htmlFor={id} className="label">
            <InputLabel text={labelText} required={required} className={styles.label} />
            {labelLink}
          </label>
          {tooltip &&
            wrapWithTooltip(
              // Not a button and not focusable; see ui/forms/Label for why, and why the click is
              // contained rather than allowed to reach whatever encloses the group.
              <span className={styles.tooltipIconWrapper} aria-hidden onClick={(event) => event.stopPropagation()}>
                <IconInfoLine className="tooltip" />
              </span>,
              tooltip,
            )}
        </div>
      )}
      <div>{children}</div>
      {errorText && <div className="error-text">{errorText}</div>}
    </div>
  );
}
